import sdk from './instance';
import { createQueryKey, localStorageUtils, logger } from './helpers';
import { profileQueryKey } from './api';
import queryClient from './query-client';
import type { StoredJwt } from './types';

class AuthService {
  static readonly storageKey = 'auth_jwt';

  async authWithCredentials(
    email: string,
    password: string,
  ): Promise<StoredJwt> {
    try {
      const response = await sdk.requests.login({
        email,
        password,
      });

      AuthService.saveJwt(response);
      return response;
    } catch (error) {
      logger.error('Error credentials:', error);
      throw error;
    }
  }

  async refreshToken(): Promise<StoredJwt | null> {
    try {
      const storedToken = this.getStoredJwt();

      if (!storedToken?.refresh_token) {
        logger.warn('Doesnt exist refresh token');
        return null;
      }

      const response = await sdk.requests.refreshTokens({
        refresh_token: storedToken.refresh_token,
      });

      AuthService.saveJwt(response);
      return response;
    } catch (error) {
      logger.error('Error refresh token:', error);
      AuthService.clearJwt();
      throw error;
    }
  }

  async logout() {
    const storedToken = this.getStoredJwt();

    try {
      if (storedToken?.refresh_token) {
        await sdk.requests.logout({
          refresh_token: storedToken.refresh_token,
        });
      }
    } catch (error) {
      logger.error('Error logout:', error);
      throw error;
    } finally {
      this.logoutSync();
    }
  }

  logoutSync() {
    AuthService.clearJwt();

    queryClient.resetQueries({
      queryKey: createQueryKey({ key: profileQueryKey }),
    });
    queryClient.clear();

    window.location.replace('/login');
  }

  getStoredJwt(): StoredJwt | null {
    try {
      return localStorageUtils.get<StoredJwt>(AuthService.storageKey);
    } catch (error) {
      logger.error('Error get token:', error);
      return null;
    }
  }

  static saveJwt(jwt: StoredJwt): void {
    localStorageUtils.set(AuthService.storageKey, jwt);
  }

  static clearJwt(): void {
    localStorageUtils.remove(AuthService.storageKey);
  }
}

const clientAuth = new AuthService();

export { AuthService, clientAuth };
